import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { paramLabel } from '../../../shared/param-labels.js';
import {
  END_CHOICES,
  STATUS_LABEL,
  isEnded,
  keptForLabel,
  residentName,
  snapshotValue,
} from '../livestock-journey.js';
import { ResidentNotes } from './ResidentNotes.jsx';
import { localDateIso } from '../local-date.js';

// Every resident the keeper has recorded, in this tank or across all of
// them, with how long each was kept and what happened. The question this
// page answers: what have I kept, for how long, and what happened to it?
// (Feedback #45, 2026-09-26: "a list of the corals they purchased over
// the years... live, dead, how long, and what happened when it died".)

const KIND_FILTERS = [
  { k: '', label: 'All' },
  { k: 'coral', label: 'Corals' },
  { k: 'fish', label: 'Fish' },
  { k: 'invert', label: 'Inverts' },
];

export function mediaBase(r) {
  return r.tank_id ? `/api/tanks/${r.tank_id}/livestock/${r.id}` : `/api/livestock/${r.id}`;
}

function longDate(v) {
  if (!v) return null;
  const dayOnly = String(v).length === 10;
  const d = new Date(dayOnly ? `${v}T12:00:00Z` : v);
  if (Number.isNaN(d.getTime())) return null;
  // A date-only value is a calendar day: read in UTC so UTC+13 keeps it.
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(dayOnly ? { timeZone: 'UTC' } : {}),
  });
}

// added_at / ended_at are calendar days, but postgres.js sends a DATE as
// midnight UTC ("2026-09-02T00:00:00.000Z"): read as a moment, that was the
// evening before west of UTC, and a chromis added Sep 2 read "Added Sep 1".
export function calendarDay(v) {
  if (!v) return null;
  const day = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? longDate(day) : longDate(v);
}

export function LivestockHistory({ tank, onClose, backLabel = 'Home' }) {
  const [scope, setScope] = useState('tank');
  // All by default: a fish-only or quarantine tank opened on Corals read
  // "Nothing recorded here yet" to a keeper whose fish had just died.
  const [kind, setKind] = useState('');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);
  const shared = tank?.role && tank.role !== 'owner';

  // Switching "This tank" / "All my tanks" quickly leaves two requests in
  // flight; only the latest may write, or the slower one shows the wrong list.
  const loadSeq = useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current;
    setErr('');
    try {
      const url =
        shared && scope === 'tank'
          ? `/api/tanks/${tank.id}/livestock/history`
          : scope === 'tank'
            ? `/api/livestock/history?tank_id=${encodeURIComponent(tank.id)}`
            : '/api/livestock/history';
      const list = await api(url);
      if (seq !== loadSeq.current) return;
      setRows(list || []);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setErr(e.message || 'Could not load the history.');
      setRows([]);
    }
  };
  useEffect(
    () => () => {
      loadSeq.current += 1;
    },
    []
  );
  useEffect(() => {
    load();
  }, [scope, tank?.id]);

  const { current, past } = useMemo(() => {
    const list = (rows || []).filter((r) => !kind || r.kind === kind);
    return {
      current: list.filter((r) => !isEnded(r)),
      past: list.filter((r) => isEnded(r)),
    };
  }, [rows, kind]);

  return (
    <div className="lb-page" aria-labelledby="lh-title">
      <header className="lb-page-header">
        <button
          type="button"
          aria-label={`Back to ${backLabel.toLowerCase()}`}
          className="ts-back"
          onClick={onClose}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{backLabel}</span>
        </button>
        <div className="ts-page-title">
          <div className="lb-eyebrow">Livestock</div>
          <h2 id="lh-title" className="font-heading lb-title">
            History
          </h2>
        </div>
      </header>

      <div className="lb-page-body">
        <div className="lb-tabs" role="tablist" aria-label="Which tanks">
          {[
            { k: 'tank', label: tank?.name || 'This tank' },
            { k: 'all', label: 'All my tanks' },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              role="tab"
              aria-selected={scope === t.k}
              className={`lb-tab ${scope === t.k ? 'active' : ''}`}
              onClick={() => setScope(t.k)}
              disabled={shared && t.k === 'all'}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="lb-cats" role="group" aria-label="Kind">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.k}
              type="button"
              className={`lb-cat ${kind === f.k ? 'active' : ''}`}
              aria-pressed={kind === f.k}
              onClick={() => setKind(f.k)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {err && (
          <p className="hv-resident-err" role="alert">
            {err}
          </p>
        )}
        {rows == null ? (
          <p className="lh-empty">Loading…</p>
        ) : current.length + past.length === 0 && kind && rows.length > 0 ? (
          <p className="lh-empty">
            No {(KIND_FILTERS.find((f) => f.k === kind)?.label || '').toLowerCase()} here. Tap All
            to see everything.
          </p>
        ) : current.length + past.length === 0 ? (
          <p className="lh-empty">
            Nothing recorded here yet. Everything you add to a tank shows up here, and stays here
            after it dies or leaves the tank.
          </p>
        ) : (
          <>
            <HistoryGroup
              title={`In the tank now (${current.length})`}
              rows={current}
              showTank={scope === 'all'}
              onOpen={setOpen}
            />
            <HistoryGroup
              title={`No longer in the tank (${past.length})`}
              rows={past}
              showTank={scope === 'all'}
              onOpen={setOpen}
            />
          </>
        )}
      </div>

      {open && (
        <JourneySheet
          resident={open}
          canWrite={tank?.role !== 'viewer'}
          isOwner={!shared}
          fallbackUnits={tank?.display_units || null}
          onClose={() => setOpen(null)}
          onChanged={async () => {
            await load();
          }}
        />
      )}
    </div>
  );
}

function HistoryGroup({ title, rows, showTank, onOpen }) {
  if (!rows.length) return null;
  return (
    <section className="lh-group">
      <div className="lh-group-title font-heading">{title}</div>
      <ul className="lh-list">
        {rows.map((r) => {
          const kept = keptForLabel(r.added_at, isEnded(r) ? r.ended_at : null);
          return (
            <li key={r.id}>
              <button type="button" className="lh-row" onClick={() => onOpen(r)}>
                <span className="lh-thumb" aria-hidden="true">
                  {r.cover_image_id ? (
                    <img src={`${mediaBase(r)}/image/${r.cover_image_id}`} alt="" loading="lazy" />
                  ) : r.catalog_image_url ? (
                    <img src={r.catalog_image_url} alt="" loading="lazy" />
                  ) : null}
                </span>
                <span className="lh-body">
                  <span className="lh-name">
                    {residentName(r)}
                    {Number(r.qty) > 1 ? ` × ${r.qty}` : ''}
                  </span>
                  <span className="lh-meta">
                    {isEnded(r) && (
                      <span className={`lh-pill lh-pill-${r.status}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    )}
                    {kept && (isEnded(r) ? `Kept ${kept}` : `In the tank ${kept}`)}
                    {showTank && r.tank_name ? ` · ${r.tank_name}` : ''}
                  </span>
                  {(r.source || r.frag_of_name) && (
                    <span className="lh-meta">
                      {r.frag_of_name ? `Cut from ${r.frag_of_name}` : `From ${r.source}`}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function JourneySheet({
  resident,
  canWrite = true,
  isOwner = false,
  fallbackUnits,
  onClose,
  onChanged,
}) {
  const [r, setR] = useState(resident);
  const [images, setImages] = useState([]);
  const [before, setBefore] = useState(null);
  const [note, setNote] = useState(resident.end_note || '');
  const [status, setStatus] = useState(resident.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const ended = isEnded(r);
  const dayOf = (d) => (d ? String(d).slice(0, 10) : '');
  const [addedOn, setAddedOn] = useState(dayOf(resident.added_at));
  const [dateMsg, setDateMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    api(`${mediaBase(r)}/images`)
      .then((list) => alive && setImages(list || []))
      .catch(() => alive && setImages([]));
    if (ended) {
      api(`${mediaBase(r)}/end-readings`)
        .then((b) => alive && setBefore(b))
        .catch(() => alive && setBefore(null));
    }
    return () => {
      alive = false;
    };
  }, [r.id]);

  const photos = [...images].sort((a, b) =>
    String(a.updated_at || '').localeCompare(String(b.updated_at || ''))
  );
  const kept = keptForLabel(r.added_at, ended ? r.ended_at : null);

  const saveEdit = async () => {
    setBusy(true);
    setErr('');
    setSaved('');
    try {
      // The tank path works for anyone the owner invited to edit; the
      // account path is only for a tank that was deleted.
      const out = r.tank_id
        ? await api(`/api/tanks/${r.tank_id}/livestock/${r.id}/end`, {
            method: 'POST',
            body: {
              status,
              ended_at: r.ended_at ? String(r.ended_at).slice(0, 10) : null,
              note: note.trim() || null,
            },
          })
        : await api(`/api/livestock/${r.id}`, {
            method: 'PATCH',
            body: { status, end_note: note.trim() || null },
          });
      const row = out?.ended || out;
      setR({ ...r, ...row });
      setSaved('Saved.');
      await onChanged();
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  // The tank path works for anyone the owner invited to edit; the account
  // path is only for a tank that was deleted.
  const saveAdded = async () => {
    if (!addedOn || addedOn === dayOf(r.added_at)) return;
    setBusy(true);
    setDateMsg(null);
    try {
      const row = await api(
        r.tank_id ? `/api/tanks/${r.tank_id}/livestock/${r.id}` : `/api/livestock/${r.id}`,
        { method: 'PATCH', body: { added_at: addedOn } }
      );
      setR({ ...r, ...row });
      setDateMsg({ ok: true, text: 'Date saved.' });
      await onChanged();
    } catch (e) {
      setDateMsg({ ok: false, text: e.message || 'Could not save the date.' });
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setErr('');
    try {
      const row = await api(`/api/tanks/${r.tank_id}/livestock/${r.id}/restore`, {
        method: 'POST',
      });
      setR({ ...r, ...row });
      setStatus('in_tank');
      await onChanged();
    } catch (e) {
      setErr(e.message || 'Could not put it back.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="hv-resident-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={residentName(r)}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="hv-resident-sheet">
        <header className="hv-resident-sheet-head">
          <div>
            <div className="hv-resident-sheet-eyebrow font-heading">
              {ended ? STATUS_LABEL[r.status] || r.status : 'In the tank'}
              {r.tank_name ? ` · ${r.tank_name}` : ''}
            </div>
            <h3 className="hv-resident-sheet-title">{residentName(r)}</h3>
            {r.scientific_name && <div className="hv-resident-sheet-sci">{r.scientific_name}</div>}
          </div>
          <button
            type="button"
            className="hv-resident-sheet-close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <ol className="lh-timeline">
          <li>
            <strong>Added</strong> {calendarDay(r.added_at) || 'date not recorded'}
            {r.source ? `, from ${r.source}` : ''}
            {r.frag_of_name ? `, cut from ${r.frag_of_name}` : ''}
          </li>
          {ended && (
            <li>
              <strong>{STATUS_LABEL[r.status] || r.status}</strong> {calendarDay(r.ended_at) || ''}
              {kept ? `, kept ${kept}` : ''}
            </li>
          )}
          {!ended && kept && <li>In the tank {kept}</li>}
        </ol>

        {canWrite && (
          <div className="lh-added-edit">
            <label className="rj-field">
              <span className="rj-label font-heading">Date added</span>
              <input
                type="date"
                max={ended && r.ended_at ? dayOf(r.ended_at) : localDateIso()}
                value={addedOn}
                onChange={(e) => setAddedOn(e.target.value)}
                disabled={busy}
              />
            </label>
            {addedOn && addedOn !== dayOf(r.added_at) && (
              <button
                type="button"
                className="rj-secondary font-heading"
                onClick={saveAdded}
                disabled={busy}
              >
                Save date
              </button>
            )}
            {dateMsg && (
              <p
                className={dateMsg.ok ? 'rj-ok' : 'hv-resident-err'}
                role={dateMsg.ok ? undefined : 'alert'}
              >
                {dateMsg.text}
              </p>
            )}
          </div>
        )}

        <div className="hv-resident-gallery-eyebrow font-heading">Photos</div>
        {photos.length ? (
          <div className="lh-photos">
            {photos.map((p) => (
              <figure key={p.id} className="lh-photo">
                <img src={`${mediaBase(r)}/image/${p.id}`} alt="" loading="lazy" />
                <figcaption>{longDate(p.updated_at)}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p className="lh-empty">
            No photos yet.{' '}
            {!ended && 'Add them from its card on Home to see how it changes over time.'}
          </p>
        )}

        {r.notes && (
          <p className="res-journey-line res-journey-first">
            <span className="rj-label font-heading">Noted when added</span>
            {r.notes}
          </p>
        )}
        <ResidentNotes
          base={mediaBase(r)}
          canWrite={canWrite}
          isOwner={isOwner}
          addedAt={r.added_at}
          disabled={busy}
          onPhotosChanged={async () => {
            setImages((await api(`${mediaBase(r)}/images`).catch(() => [])) || []);
          }}
        />

        {ended && before?.readings?.length > 0 && (
          <>
            <div className="hv-resident-gallery-eyebrow font-heading">
              Your readings in the {before.days} days before
            </div>
            <ul className="lh-readings">
              {before.readings.map((x) => (
                <li key={x.param}>
                  <span>{paramLabel(x.param)}</span>
                  <span className="font-mono">
                    {snapshotValue(x.param, x.value, r.tank_display_units || fallbackUnits)}
                  </span>
                  <span className="lh-reading-date">{longDate(x.ts)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {ended && before && !before.readings?.length && (
          <p className="lh-empty">No readings were logged in the week before.</p>
        )}

        {ended && !canWrite && r.end_note && (
          <p className="res-journey-line">
            <span className="rj-label font-heading">What happened</span>
            {r.end_note}
          </p>
        )}
        {ended && canWrite && (
          <>
            <div className="rj-label font-heading">What happened</div>
            <div className="rj-choices" role="radiogroup" aria-label="What happened">
              {END_CHOICES.map((c) => (
                <button
                  key={c.k}
                  type="button"
                  role="radio"
                  aria-checked={status === c.k}
                  className={`rj-choice${status === c.k ? ' is-on' : ''}`}
                  onClick={() => setStatus(c.k)}
                  disabled={busy}
                >
                  <span className="rj-choice-label">{c.label}</span>
                </button>
              ))}
            </div>
            <label className="rj-field">
              <span className="rj-label font-heading">What happened, in your words</span>
              <textarea
                rows={4}
                maxLength={1000}
                value={note}
                placeholder="What happened, in your own words."
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
              />
            </label>
          </>
        )}

        {err && (
          <p className="hv-resident-err" role="alert">
            {err}
          </p>
        )}
        {saved && <p className="rj-ok">{saved}</p>}

        {ended && canWrite && (
          <div className="hv-resident-actions">
            {r.tank_id && (
              <button
                type="button"
                className="hv-resident-action font-heading"
                onClick={restore}
                disabled={busy}
              >
                Back in the tank
              </button>
            )}
            <button
              type="button"
              className="hv-resident-action save font-heading"
              onClick={saveEdit}
              disabled={busy}
            >
              {busy ? '…' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
